import {useMemo} from 'react';
import {useLocation} from 'react-router-dom';
import {useIVStore} from '@chekin/shared';
import {useIVComputedDetails, useIVRegistrationGate} from 'hooks';
import {useGetGuestId} from 'hooks/useGetGuestId';
import {useReservation} from 'context/reservation';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';
import {useIVOnProperty} from 'hooks/useIVOnProperty';
import {PATHS} from 'routes/constants';

export type RegistrationStepKey =
  | 'presence'
  | 'contacts'
  | 'documentSelection'
  | 'document'
  | 'selfie'
  | 'form';

export type RegistrationStepState = {
  step: number;
  totalSteps: number;
  activeIndex: number;
  stepKeys: RegistrationStepKey[];
  skippedIndices: number[];
};

const SEGMENT_TO_KEY: Record<string, RegistrationStepKey> = {
  [PATHS.verification.qrScan]: 'presence',
  [PATHS.verification.contacts]: 'contacts',
  [PATHS.verification.documentSelection]: 'documentSelection',
  [PATHS.verification.front]: 'document',
  [PATHS.verification.back]: 'document',
  [PATHS.verification.selfie]: 'selfie',
};

function buildRegistrationStepKeys(
  biomatch?: boolean,
  includeForm = true,
  includeContacts = false,
  includePresence = false,
): RegistrationStepKey[] {
  return [
    ...(includePresence ? (['presence'] as const) : []),
    ...(includeContacts ? (['contacts'] as const) : []),
    'documentSelection',
    'document',
    ...(biomatch ? (['selfie'] as const) : []),
    ...(includeForm ? (['form'] as const) : []),
  ];
}

export function useRegistrationStep(): RegistrationStepState | null {
  const {pathname} = useLocation();
  const {biomatch, isIVEnabled, isIVCompleted, isUnderageIvBypassDeclared} =
    useIVComputedDetails();
  const {isIVRequiredForGuest} = useIVRegistrationGate();
  const isIvSkippedInStore = useIVStore(state => state.isIvSkipped);
  const isExistingGuest = Boolean(useGetGuestId());
  const {housing} = useReservation();
  const {isContactVerificationRequired} = useVerificationSettings(housing?.id);
  const {isIVOnPropertyEnabled} = useIVOnProperty();

  const stepKeys = useMemo<RegistrationStepKey[]>(
    () =>
      buildRegistrationStepKeys(
        biomatch,
        !isExistingGuest,
        isContactVerificationRequired,
        isIVOnPropertyEnabled,
      ),
    [biomatch, isExistingGuest, isContactVerificationRequired, isIVOnPropertyEnabled],
  );

  const isIVSkipped =
    isIvSkippedInStore &&
    isIVRequiredForGuest &&
    !isIVCompleted &&
    !isUnderageIvBypassDeclared;

  const activeIndex = useMemo(() => {
    if (pathname.includes(`/${PATHS.addPersonalDataForm}`)) {
      return isIVCompleted || isIVSkipped ? stepKeys.indexOf('form') : -1;
    }

    const segment = pathname.split('/').filter(Boolean).pop() ?? '';
    const key = SEGMENT_TO_KEY[segment];

    return key ? stepKeys.indexOf(key) : -1;
  }, [pathname, stepKeys, isIVCompleted, isIVSkipped]);

  const isFormSkippedStep = isIVSkipped && stepKeys[activeIndex] === 'form';

  const skippedIndices = useMemo(
    () =>
      isFormSkippedStep ? Array.from({length: activeIndex}, (_, index) => index) : [],
    [isFormSkippedStep, activeIndex],
  );

  if (!isIVEnabled || activeIndex < 0) return null;

  return {
    step: activeIndex + 1,
    totalSteps: stepKeys.length,
    activeIndex,
    stepKeys,
    skippedIndices,
  };
}
